import * as React from 'react';
import { IntersectionOptions, useInView } from 'react-intersection-observer';
import { useWillUnmountEffect } from '@tock/hooks';

interface InView<T> extends Omit<IntersectionOptions, 'onChange'> {
  onChange: (inView: boolean, value?: T) => void;
}

export interface ScrollListNavigationPanelProps {
  className?: string;
  value?: string | number;
  component?: React.ElementType;
  inView?: InView<string | number>;
  idGenerator?(value?: string | number): string;
}

/**
 * ScrollListNavigationPanel is a panel for the ScrollListNavigation component to work with
 *
 * ```js
 * import { ScrollListNavigationPanel } from 'components/business/ScrollListNavigationPanel';
 * ```
 */
const ScrollListNavigationPanel: React.FunctionComponent<
  React.PropsWithChildren<ScrollListNavigationPanelProps>
> = ({
  children,
  className,
  value,
  component = 'div',
  inView,
  idGenerator = (value) => `${value}-panel`,
  ...rest
}) => {
  const { onChange: onChangeInView, ...inViewOptions } = inView ?? {};
  const [inViewRef, isInView] = useInView(inViewOptions);

  React.useEffect(() => {
    onChangeInView ? onChangeInView(isInView, value) : undefined;
    // This wants us to declare onChangeInView as a dep, but that causes infinite loop here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView]);

  useWillUnmountEffect(() => {
    onChangeInView ? onChangeInView(false, value) : undefined;
  });

  const Component = component;
  return (
    <Component ref={inViewRef} className={className} id={idGenerator(value)} {...rest}>
      {children}
    </Component>
  );
};

export default ScrollListNavigationPanel;
